// presentation/configuration/AppInitializer.ts
import { ref } from 'vue'
import { useAuthStore } from '@/presentation/stores/auth'
import { useProfileStore } from '@/presentation/stores/profile/profile.ts'
import { useSchoolYearStore } from '@/presentation/stores/school/schoolYear'
import { NabooError } from '@/core/infrastructure/services/NabooError'
import { storeEventBus } from '@/presentation/stores/utils/storeEventBus'
import { useToastService } from '@/presentation/configuration/ToastService.ts'
import { useSchoolStore } from '@/presentation/stores/school/school.ts'

const ToastService = useToastService()

/**
 * Service d'initialisation de l'application
 * Orchestre la séquence de démarrage et l'initialisation des données
 */
export class AppInitializer {
  private static instance: AppInitializer
  private initialized = ref(false)
  private initializing = ref(false)
  private error = ref<NabooError | null>(null)

  private constructor() {
    // S'abonner aux événements d'authentification
    storeEventBus.on('auth-initialized', this.handleAuthInitialized.bind(this))
    storeEventBus.on('auth-failed', this.handleAuthFailed.bind(this))
    storeEventBus.on('auth-logged-out', this.handleAuthLogout.bind(this))
  }

  /**
   * Obtient l'instance singleton
   */
  public static getInstance(): AppInitializer {
    if (!AppInitializer.instance) {
      AppInitializer.instance = new AppInitializer()
    }
    return AppInitializer.instance
  }

  /**
   * Initialise l'application
   */
  public async initialize(): Promise<boolean> {
    // Si déjà initialisé ou en cours d'initialisation, retourne l'état actuel
    if (this.initialized.value) return true
    if (this.initializing.value) return false

    this.initializing.value = true
    this.error.value = null

    try {
      // Étape 1: Initialiser l'authentification
      const authStore = useAuthStore()
      const authResult = await authStore.initAuth()

      // Si l'authentification échoue, on s'arrête là
      if (!authResult) {
        this.initializing.value = false
        return false
      }

      // Note: Les étapes suivantes sont déclenchées par l'événement 'auth-initialized'
      // via la méthode handleAuthInitialized ci-dessous

      return true
    } catch (error) {
      console.error("Erreur lors de l'initialisation de l'application:", error)

      if (error instanceof NabooError) {
        this.error.value = error
      } else {
        this.error.value = new NabooError({
          message: "Une erreur est survenue lors de l'initialisation de l'application",
          status: 500,
          name: 'InitializationError',
          code: 'APP_INIT_ERROR',
          details: error
        })
      }

      ToastService.showToast({
        severity: 'error',
        summary: "Erreur d'initialisation",
        detail: this.error.value.getUserFriendlyError().message
      })

      // Émettre un événement d'échec
      storeEventBus.emit('app-initialization-failed', {
        error: this.error.value.message
      })

      return false
    } finally {
      this.initializing.value = false
    }
  }

  /**
   * Gère l'événement d'authentification réussie
   */
  private async handleAuthInitialized(payload: { authenticated: boolean }): Promise<void> {
    if (!payload.authenticated) return

    try {
      // Étape 2: Charger le profil utilisateur
      const profileStore = useProfileStore()
      await profileStore.getMe()

      // Étape 3: Charger les années scolaires
      const schoolYearStore = useSchoolYearStore()
      await schoolYearStore.fetchAllSchoolYears()

      // Autres initialisations nécessaires...
      const schoolStore = useSchoolStore()
      await schoolStore.fetchSchoolsForMe()

      // Marquer comme initialisé
      this.initialized.value = true

      // Émettre un événement de succès
      storeEventBus.emit('app-initialized', { success: true })
    } catch (error) {
      console.error('Erreur lors du chargement des données initiales:', error)

      // Émettre un événement d'échec
      storeEventBus.emit('app-initialization-failed', {
        error: error instanceof Error ? error.message : String(error)
      })

      if (error instanceof NabooError) {
        this.error.value = error
      } else {
        this.error.value = new NabooError({
          message: 'Une erreur est survenue lors du chargement des données initiales',
          status: 500,
          name: 'DataInitializationError',
          code: 'APP_DATA_INIT_ERROR',
          details: error
        })
      }

      ToastService.showToast({
        severity: 'error',
        summary: 'Erreur de chargement',
        detail: this.error.value.getUserFriendlyError().message
      })
    }
  }

  /**
   * Gère l'événement d'échec d'authentification
   */
  private handleAuthFailed(payload: { error: string }): void {
    console.error("Erreur d'authentification:", payload.error)
    this.error.value = new NabooError({
      message: payload.error,
      status: 401,
      name: 'AuthenticationError',
      code: 'AUTH_FAILED'
    })

    // Émettre un événement d'échec d'initialisation de l'application
    storeEventBus.emit('app-initialization-failed', {
      error: payload.error
    })
  }

  /**
   * Gère l'événement de déconnexion
   */
  private handleAuthLogout(): void {
    // Réinitialiser l'état
    this.reset()
  }

  /**
   * Vérifie si l'application est initialisée
   */
  public isInitialized(): boolean {
    return this.initialized.value
  }

  /**
   * Vérifie si l'application est en cours d'initialisation
   */
  public isInitializing(): boolean {
    return this.initializing.value
  }

  /**
   * Récupère l'erreur d'initialisation, le cas échéant
   */
  public getError(): NabooError | null {
    return this.error.value
  }

  /**
   * Réinitialise l'état de l'initialisation
   */
  public reset(): void {
    this.initialized.value = false
    this.initializing.value = false
    this.error.value = null
  }
}

/**
 * Hook composable pour utiliser l'initialisateur d'application
 */
export function useAppInitializer() {
  return AppInitializer.getInstance()
}
